import React, { useEffect, useState, useCallback } from "react";
import Linkedin from "../../Atomos/Icons/ViewMode/Linkedin";
import Facebook from "../../Atomos/Icons/ViewMode/Facebook";
import XCircled from "../../Atomos/Icons/XCircled";
import Instagram from "../../Atomos/Icons/ViewMode/Instagram";
import Youtube from "../../Atomos/Icons/ViewMode/Youtube";
import FirmLocationsModal from "../../Moleculas/FirmLocationsModal";
import { getLocationsByFirm } from "../../../Services/Office";
import type { IOfficeNew } from "../../../interfaces/models/IOffice";
import { Link } from "react-router-dom";
import { formatLocationText } from "../../../utils/functions";
import { slugifyUrlSegment } from "../../../utils/publicProfileUrls";

export interface IFirmCard {
  id: string;
  legacyId?: string;
  logo: string | undefined;
  name: string;
  mainOffice: string;
  linkedinUrl?: string;
  xUrl?: string;
  facebookUrl?: string;
  instagramUrl?: string;
  youtubeUrl?: string;
  locations?: Array<{
    id: number;
    city: string;
    country: string;
    address: string;
    cityState: string;
    zipCode: string;
  }>;
  hasMoreOffices?: boolean;
}

interface ISocialMedia {
  icon: JSX.Element;
  type: keyof Pick<
    IFirmCard,
    "linkedinUrl" | "xUrl" | "facebookUrl" | "instagramUrl" | "youtubeUrl"
  >;
}

const socialMedia: ISocialMedia[] = [
  { icon: <Linkedin />, type: "linkedinUrl" },
  { icon: <XCircled />, type: "xUrl" },
  { icon: <Facebook />, type: "facebookUrl" },
  { icon: <Instagram />, type: "instagramUrl" },
  { icon: <Youtube />, type: "youtubeUrl" },
];

const FirmFrame: React.FC<IFirmCard> = ({
  id,
  legacyId,
  logo,
  name,
  mainOffice,
  linkedinUrl,
  xUrl,
  facebookUrl,
  instagramUrl,
  youtubeUrl,
  hasMoreOffices = true,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [locations, setLocations] = useState<IOfficeNew[]>([]);
  const [totalLocations, setTotalLocations] = useState(0);
  const urls = { linkedinUrl, xUrl, facebookUrl, instagramUrl, youtubeUrl };
  const MAX_VISIBLE_LOCATIONS = 2;

  const sortOffices = (offices: IOfficeNew[]) =>
    [...offices].sort((left, right) => {
      const leftIsMain = String(left.officeType) === "Main";
      const rightIsMain = String(right.officeType) === "Main";
      if (leftIsMain && !rightIsMain) {
        return -1;
      }
      if (!leftIsMain && rightIsMain) {
        return 1;
      }
      return formatLocationText(left).localeCompare(formatLocationText(right));
    });

  const getLocations = useCallback(async () => {
    const response = await getLocationsByFirm(id);
    const sorted = sortOffices(response);
    setTotalLocations(sorted.length);
    setLocations(sorted.slice(0, MAX_VISIBLE_LOCATIONS));
  }, [id]);

  useEffect(() => {
    if (!id) {
      return;
    }
    getLocations();
  }, [id, getLocations]);

  return (
    <div className="flex gap-6">
      <img
        src={logo}
        alt=""
        className="p-2 h-[3.5rem] w-[3.5rem] lg:h-[7.5rem] lg:w-[7.5rem] rounded-full object-contain shrink-0 bg-white outline outline-gray-200"
      />

      <div className="flex flex-col flex-1 min-w-0 text-start">
        <div className=" text-lg lg:text-xl font-bold truncate text-primary">
          {name}
        </div>

        <div className="flex flex-col flex-1 text-sm lg:text-base">
          <div className="flex flex-col overflow-y-auto text-gray-font">
            {locations.length > 0
              ? locations.map((location) => (
                  <Link
                    key={location.id}
                    to={`/member/law-firm/${slugifyUrlSegment(name)}/${location.id}`}
                  >
                    {formatLocationText(location)}
                  </Link>
                ))
              : mainOffice && (
                  <Link
                    to={`/member/law-firm/${slugifyUrlSegment(name)}/${legacyId ?? id}`}
                  >
                    {mainOffice}
                  </Link>
                )}
          </div>
          {hasMoreOffices && totalLocations > MAX_VISIBLE_LOCATIONS && (
            <button
              onClick={() => setIsModalOpen(true)}
              className={`text-left text-secondary justify-start transition-colors mb-2 hover:text-gray-600`}
            >
              {`More Locations  +`}
            </button>
          )}
          {locations.length < 1 && <div className="mb-2" />}
        </div>

        <div className="flex h-6 justify-between w-full">
          {socialMedia.map(({ icon, type }) => {
            const url = urls[type];
            return (
              <button
                key={type}
                disabled={!url}
                onClick={() => url && window.open(url, "_blank")}
                className={`w-6 h-6 ${
                  !url ? "text-gray-200" : "text-gray-font hover:text-primary"
                }`}
              >
                {icon}
              </button>
            );
          })}
        </div>
      </div>

      <FirmLocationsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        firmId={id}
        firmName={name}
      />
    </div>
  );
};

export default FirmFrame;
